import { ref, computed, onMounted } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { workflowService } from '@/services/workflowService.js'
import { prospectPreferencesService } from '@/services/prospectService.js'
import { employeeService, teamService } from '@/services/employeeService.js'
import { hoursService } from '@/services/hoursService.js'
import { useAuth } from '../auth/useAuth'
import { invalidateNavBadges } from '../ui/useNavBadges'
import { isHiddenTeam } from '@/constants/teams.js'
import {
  conceptStorageKey,
  saveConcept,
  loadConcept,
  clearConcept
} from '@/utils/conceptStorage.js'
import {
  getWorkflowStatusLabel as _getWorkflowLabel,
  getWorkflowStatusBadgeClass
} from '@/utils/statusBadge'

export function useBeoordelen() {
  const { user, userRole, userTeamIds: myTeamIds } = useAuth()
  const queryClient = useQueryClient()

  // ==================== Tab & Loading state ====================
  const activeTab = ref('all-pending')
  const isLoading = ref(true)
  const urgentProspects = ref([])
  const pendingProspects = ref([])
  const recentProspects = ref([])

  // My pending: filtered by PL's assigned teams
  const myPendingProspects = computed(() =>
    pendingProspects.value.filter(p => myTeamIds.value.includes(p.responsible_team_id))
  )

  // Tabs configuration
  const tabs = computed(() => [
    {
      key: 'urgent',
      label: 'Spoed',
      count: isLoading.value ? undefined : urgentProspects.value.length,
      badgeClass:
        urgentProspects.value.length > 0
          ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
          : undefined
    },
    {
      key: 'my-pending',
      label: 'Mijn beoordelingen',
      count: isLoading.value ? undefined : myPendingProspects.value.length
    },
    {
      key: 'all-pending',
      label: 'Alle beoordelingen',
      count: isLoading.value ? undefined : pendingProspects.value.length
    },
    {
      key: 'recent',
      label: 'Recent beoordeeld'
    }
  ])

  // ==================== Modal state ====================
  const selectedProspect = ref(null)
  const selectedPreferences = ref([])
  const loadingPreferences = ref(false)
  const reviewFeedback = ref('')
  const isProcessing = ref(false)
  const expandedPreferences = ref({})

  // Add role state
  const isAddingRole = ref(false)
  const addRoleError = ref('')
  const newlyAddedPrefId = ref(null)

  // Employee data
  const employeeMap = ref({})
  const employeeDetails = ref({})
  const employeeAvailability = ref({})

  // Teams data
  const teams = ref([])

  // PL Selections state: { preferenceId: { employeeId, dpw, name, isCustom } }
  const plSelections = ref({})

  // Availability section expanded state per preference
  const availabilityExpanded = ref({})

  // Employee modal state
  const showEmployeeModal = ref(false)
  const currentPreference = ref(null)
  const currentPreferenceTeam = ref('')
  const currentPreferenceFunction = ref('')
  const currentPreferenceDpw = ref(1.0)
  const currentPreferencePeriodStart = ref(null)
  const currentPreferencePeriodEnd = ref(null)

  // Extra comparison employees per preference: { preferenceId: [{ employeeId, dpw, name }] }
  const comparisonEmployees = ref({})

  // Per-employee graph expansion state: { preferenceId: { employeeId: boolean } }
  const employeeGraphExpanded = ref({})

  // Local "concept" draft state (per user, localStorage only)
  const hasActiveConcept = ref(false)
  const conceptLoaded = ref(false)
  const conceptSavedAt = ref(null)

  // True while the PL has made employee-selection changes in the open modal
  // that are not yet persisted as a local concept.
  const hasUnsavedChanges = ref(false)

  // ==================== Computed ====================
  const currentPreferenceId = computed(() => currentPreference.value?.id ?? null)

  const allRolesHaveSelection = computed(() => {
    if (selectedPreferences.value.length === 0) return false
    return selectedPreferences.value.every(pref => plSelections.value[pref.id]?.employeeId)
  })

  // Lokale "voorgesteld" allocaties voor de open prospect: alle mdw-rol koppelingen
  // die de PL tijdens de review heeft gemaakt (via radio of via "Andere medewerker
  // toevoegen"), nog niet gepersisteerd. Gebruikt door PreferenceReviewCard om
  // matchPercentVoorgesteld op andere rollen te corrigeren wanneer dezelfde mdw aan
  // meerdere rollen wordt gekoppeld. Per (rol, mdw) wordt maar één entry toegevoegd
  // — als zowel plSelection als comparisonEmployees dezelfde mdw bevatten telt hij
  // niet dubbel.
  const localProposals = computed(() => {
    const list = []
    const prospect = selectedProspect.value
    if (!prospect) return list
    for (const pref of selectedPreferences.value) {
      const periodStart = pref.start_date || prospect.start_date || null
      const periodEnd = pref.end_date || prospect.expected_closing_date || null
      const seen = new Set()
      const sel = plSelections.value[pref.id]
      if (sel?.employeeId) {
        seen.add(sel.employeeId)
        list.push({
          employeeId: sel.employeeId,
          preferenceId: pref.id,
          role: pref.role || null,
          dpw: Number(sel.dpw) || Number(pref.dpw) || 0,
          periodStart,
          periodEnd
        })
      }
      const comparison = comparisonEmployees.value[pref.id] || []
      for (const comp of comparison) {
        if (!comp.employeeId || seen.has(comp.employeeId)) continue
        seen.add(comp.employeeId)
        list.push({
          employeeId: comp.employeeId,
          preferenceId: pref.id,
          role: pref.role || null,
          dpw: Number(comp.dpw) || Number(pref.dpw) || 0,
          periodStart,
          periodEnd
        })
      }
    }
    return list
  })

  // ==================== Team helpers ====================
  const isMyTeamProspect = prospect => {
    if (userRole.value !== 'performance_lead' || myTeamIds.value.length === 0) return true
    return !prospect.responsible_team_id || myTeamIds.value.includes(prospect.responsible_team_id)
  }

  const getTeamCardClass = prospect => {
    if (userRole.value !== 'performance_lead' || myTeamIds.value.length === 0) return ''
    if (isMyTeamProspect(prospect)) return 'ring-2 ring-blue-500'
    return 'opacity-60'
  }

  // ==================== Data loading ====================
  const loadData = async () => {
    isLoading.value = true
    try {
      const [urgent, pending, recent] = await Promise.all([
        workflowService.getUrgent(),
        workflowService.getPending(),
        workflowService.getRecent(10)
      ])
      urgentProspects.value = urgent.filter(
        prospect => !isHiddenTeam(prospect.responsible_team_name)
      )
      pendingProspects.value = pending.filter(
        prospect => !isHiddenTeam(prospect.responsible_team_name)
      )
      recentProspects.value = recent.filter(
        prospect => !isHiddenTeam(prospect.responsible_team_name)
      )

      // Set default tab based on available items
      if (urgent.length > 0) {
        activeTab.value = 'urgent'
      } else if (
        userRole.value === 'performance_lead' &&
        myTeamIds.value.length > 0 &&
        pending.filter(p => myTeamIds.value.includes(p.responsible_team_id)).length > 0
      ) {
        activeTab.value = 'my-pending'
      } else {
        activeTab.value = 'all-pending'
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      isLoading.value = false
    }
  }

  const loadEmployees = async () => {
    try {
      const employees = await employeeService.getAll({ is_sourceable: 'true' })
      const map = {}
      const details = {}
      employees.forEach(emp => {
        map[emp.simplicate_id] = emp.name
        details[emp.simplicate_id] = emp
      })
      employeeMap.value = map
      employeeDetails.value = details
    } catch (error) {
      console.error('Error loading employees:', error)
    }
  }

  const loadTeams = async () => {
    try {
      teams.value = await teamService.getAll()
    } catch (error) {
      console.error('Error loading teams:', error)
    }
  }

  // ==================== Toggle helpers ====================
  const togglePreferenceExpanded = prefIdx => {
    expandedPreferences.value[prefIdx] = !expandedPreferences.value[prefIdx]
  }

  const toggleAvailabilityExpanded = prefId => {
    availabilityExpanded.value[prefId] = !availabilityExpanded.value[prefId]
  }

  const toggleEmployeeGraph = (prefId, empId) => {
    if (!employeeGraphExpanded.value[prefId]) {
      employeeGraphExpanded.value[prefId] = {}
    }
    const currentState = employeeGraphExpanded.value[prefId][empId] !== false
    employeeGraphExpanded.value[prefId][empId] = !currentState
  }

  // ==================== Modal ====================
  const openReviewModal = async prospect => {
    selectedProspect.value = prospect
    reviewFeedback.value = ''
    plSelections.value = {}
    expandedPreferences.value = {}
    availabilityExpanded.value = {}
    employeeAvailability.value = {}
    comparisonEmployees.value = {}
    employeeGraphExpanded.value = {}
    hasActiveConcept.value = false
    conceptLoaded.value = false
    conceptSavedAt.value = null
    hasUnsavedChanges.value = false
    loadingPreferences.value = true

    try {
      const prefs = await prospectPreferencesService.getByProspect(prospect.id)
      selectedPreferences.value = prefs.map(p => ({
        ...p,
        preferred_employees: Array.isArray(p.preferred_employees)
          ? p.preferred_employees
          : JSON.parse(p.preferred_employees || '[]'),
        teams: Array.isArray(p.teams) ? p.teams : JSON.parse(p.teams || '[]'),
        experience_levels: Array.isArray(p.experience_levels)
          ? p.experience_levels
          : JSON.parse(p.experience_levels || '[]')
      }))

      // Collapse all preferences and availability sections by default
      selectedPreferences.value.forEach((pref, idx) => {
        expandedPreferences.value[idx] = false
        availabilityExpanded.value[pref.id] = false
      })

      // Load existing PL selections if any
      selectedPreferences.value.forEach(pref => {
        if (pref.pl_selected_employee_id) {
          const isCustom = !pref.preferred_employees?.includes(pref.pl_selected_employee_id)
          plSelections.value[pref.id] = {
            employeeId: pref.pl_selected_employee_id,
            dpw: pref.pl_selected_dpw || pref.dpw,
            name: employeeMap.value[pref.pl_selected_employee_id] || pref.pl_selected_employee_id,
            isCustom
          }
        }
      })

      // Restore a locally saved concept (draft) for this prospect, if any.
      // The concept is newer than any server-persisted selection, so it wins
      // per preference. Entries for preferences that no longer exist are skipped.
      restoreConcept()

      // Load availability for all preferred employees
      await loadEmployeeAvailability(prospect)
    } catch (error) {
      console.error('Error loading preferences:', error)
      selectedPreferences.value = []
    } finally {
      loadingPreferences.value = false
    }
  }

  const closeModal = () => {
    selectedProspect.value = null
    selectedPreferences.value = []
    reviewFeedback.value = ''
    plSelections.value = {}
    expandedPreferences.value = {}
    availabilityExpanded.value = {}
    comparisonEmployees.value = {}
    employeeGraphExpanded.value = {}
    hasActiveConcept.value = false
    conceptLoaded.value = false
    conceptSavedAt.value = null
    hasUnsavedChanges.value = false
  }

  // ==================== Availability ====================
  const loadEmployeeAvailability = async (prospect, mode) => {
    if (!prospect.start_date || !prospect.expected_closing_date) return

    // Store the prospect ID at the start of the request
    const requestProspectId = prospect.id

    try {
      const startDate = prospect.start_date.split('T')[0]
      const endDate = prospect.expected_closing_date.split('T')[0]

      const params = {
        start_date: startDate,
        end_date: endDate
      }
      if (mode && mode !== 'presourcing') {
        params.availability_mode = mode
      }
      const response = await hoursService.getWeeklyAvailability(params)

      // Check if user has switched to a different prospect while we were loading
      // If so, discard this response to prevent overwriting with stale data
      if (selectedProspect.value?.id !== requestProspectId) {
        return
      }

      if (response.success && response.data) {
        const availMap = {}
        response.data.forEach(emp => {
          const matchPercent = calculateMatchPercent(emp, 1)
          availMap[emp.simplicate_id] = {
            employee: emp,
            matchPercent
          }
        })

        // For preferred employees not in the response, add placeholder entries
        const missingEmployeeIds = new Set()
        for (const pref of selectedPreferences.value) {
          const preferredEmps = pref.preferred_employees || []
          for (const empId of preferredEmps) {
            if (!availMap[empId]) {
              missingEmployeeIds.add(empId)
            }
          }
        }

        if (missingEmployeeIds.size > 0 && response.data.length > 0) {
          const templateWeeks = response.data[0].weeks || []
          const emptyWeeks = templateWeeks.map(w => ({
            ...w,
            booked_hours: 0,
            assignment_hours: 0,
            assignments: [],
            available_days: 5
          }))

          for (const empId of missingEmployeeIds) {
            if (employeeDetails.value[empId]) {
              availMap[empId] = {
                employee: {
                  ...employeeDetails.value[empId],
                  weeks: emptyWeeks,
                  max_days_per_week: 5
                },
                matchPercent: 100
              }
            }
          }
        }

        employeeAvailability.value = availMap
      }
    } catch (error) {
      console.error('Error loading availability:', error)
    }
  }

  const calculateMatchPercent = (employee, requiredDpw) => {
    if (!employee?.weeks?.length || !requiredDpw) return 0
    let matchingWeeks = 0
    employee.weeks.forEach(week => {
      if (Number(week.available_days || 0) >= requiredDpw) {
        matchingWeeks++
      }
    })
    return (matchingWeeks / employee.weeks.length) * 100
  }

  // ==================== Employee selection ====================
  const selectEmployee = (pref, selection) => {
    if (JSON.stringify(plSelections.value[pref.id]) !== JSON.stringify(selection)) {
      plSelections.value[pref.id] = selection
      hasUnsavedChanges.value = true
    }
  }

  const selectComparisonEmployee = (pref, selection) => {
    if (JSON.stringify(plSelections.value[pref.id]) !== JSON.stringify(selection)) {
      plSelections.value[pref.id] = selection
      hasUnsavedChanges.value = true
    }
  }

  const openEmployeeModal = pref => {
    currentPreference.value = pref
    currentPreferenceTeam.value = pref.teams?.[0] || ''
    currentPreferenceFunction.value = pref.experience_levels?.[0] || ''
    currentPreferenceDpw.value = Number(pref.dpw) || 1.0
    currentPreferencePeriodStart.value =
      pref.start_date || selectedProspect.value?.start_date || null
    currentPreferencePeriodEnd.value =
      pref.end_date || selectedProspect.value?.expected_closing_date || null
    showEmployeeModal.value = true
  }

  const getAssignedEmployeeIds = () => {
    if (!currentPreference.value) return []
    const prefId = currentPreference.value.id
    const preferred = currentPreference.value.preferred_employees || []
    const comparison = (comparisonEmployees.value[prefId] || []).map(e => e.employeeId)
    return [...preferred, ...comparison]
  }

  const handleEmployeeSelected = async ({ employee, dpw }) => {
    if (!currentPreference.value) return

    const prefId = currentPreference.value.id
    const empId = employee.simplicate_id

    const isInPreferred = currentPreference.value.preferred_employees?.includes(empId)

    if (!comparisonEmployees.value[prefId]) {
      comparisonEmployees.value[prefId] = []
    }
    const alreadyInComparison = comparisonEmployees.value[prefId].some(e => e.employeeId === empId)

    if (!isInPreferred && !alreadyInComparison) {
      comparisonEmployees.value[prefId].push({
        employeeId: empId,
        dpw,
        name: employee.name
      })
      hasUnsavedChanges.value = true
    }

    if (!employeeAvailability.value[empId]) {
      employeeAvailability.value[empId] = {
        employee,
        matchPercent: calculateMatchPercent(employee, dpw)
      }
    }

    showEmployeeModal.value = false
  }

  const removeComparisonEmployee = (prefId, empId) => {
    let changed = false
    if (comparisonEmployees.value[prefId]) {
      const before = comparisonEmployees.value[prefId].length
      comparisonEmployees.value[prefId] = comparisonEmployees.value[prefId].filter(
        e => e.employeeId !== empId
      )
      changed = comparisonEmployees.value[prefId].length !== before
    }
    if (plSelections.value[prefId]?.employeeId === empId && plSelections.value[prefId]?.isCustom) {
      delete plSelections.value[prefId]
      changed = true
    }
    if (changed) hasUnsavedChanges.value = true
  }

  // ==================== Concept (local draft) ====================
  const currentConceptKey = () => {
    if (!selectedProspect.value) return null
    return conceptStorageKey(user.value?.email, selectedProspect.value.id)
  }

  const restoreConcept = () => {
    const key = currentConceptKey()
    if (!key) return
    const stored = loadConcept(key)
    if (!stored) return
    hasActiveConcept.value = true
    conceptLoaded.value = true
    conceptSavedAt.value = stored.savedAt || null

    // Only apply entries for preferences that still exist; names fall back to
    // the employee map (or the stored name when the employee is unknown).
    const knownPrefIds = new Set(selectedPreferences.value.map(p => String(p.id)))
    for (const [prefId, selection] of Object.entries(stored.plSelections || {})) {
      if (!knownPrefIds.has(String(prefId))) continue
      plSelections.value[prefId] = {
        employeeId: selection.employeeId,
        dpw: Number(selection.dpw) || 0,
        name: employeeMap.value[selection.employeeId] || selection.name || selection.employeeId,
        isCustom: !!selection.isCustom
      }
    }
    for (const [prefId, entries] of Object.entries(stored.comparisonEmployees || {})) {
      if (!knownPrefIds.has(String(prefId))) continue
      comparisonEmployees.value[prefId] = (Array.isArray(entries) ? entries : [])
        .filter(entry => entry && entry.employeeId)
        .map(entry => ({
          employeeId: entry.employeeId,
          dpw: Number(entry.dpw) || 0,
          name: employeeMap.value[entry.employeeId] || entry.name || entry.employeeId
        }))
    }
  }

  const saveConceptAsDraft = () => {
    const key = currentConceptKey()
    if (!key) return false
    const payload = {
      savedAt: new Date().toISOString(),
      plSelections: plSelections.value || {},
      comparisonEmployees: comparisonEmployees.value || {}
    }
    const saved = saveConcept(key, payload)
    if (saved) {
      hasActiveConcept.value = true
      conceptSavedAt.value = payload.savedAt
      hasUnsavedChanges.value = false
    }
    return saved
  }

  const discardConcept = () => {
    const key = currentConceptKey()
    if (key) clearConcept(key)
    hasActiveConcept.value = false
    conceptLoaded.value = false
    conceptSavedAt.value = null
    hasUnsavedChanges.value = false
    plSelections.value = {}
    comparisonEmployees.value = {}
  }

  // Remove the stored concept for a prospect after a workflow action
  // completed successfully (the draft has been consumed).
  const clearStoredConcept = prospectId => {
    if (!prospectId) return
    clearConcept(conceptStorageKey(user.value?.email, prospectId))
  }

  // ==================== Save comparison employees ====================
  const saveComparisonEmployees = async () => {
    const prospectId = selectedProspect.value?.id
    if (!prospectId) return

    for (const pref of selectedPreferences.value) {
      const extras = comparisonEmployees.value[pref.id]
      if (!extras || extras.length === 0) continue

      const current = Array.isArray(pref.preferred_employees) ? pref.preferred_employees : []
      const merged = [...current]

      for (const comp of extras) {
        if (!merged.includes(comp.employeeId)) {
          merged.push(comp.employeeId)
        }
      }

      if (merged.length === current.length) continue

      await prospectPreferencesService.update(prospectId, pref.id, {
        preferred_employees: merged
      })
    }
  }

  // ==================== Team update ====================
  const handleUpdateTeam = async newTeamId => {
    if (!selectedProspect.value || !newTeamId) return
    try {
      const result = await workflowService.updateResponsibleTeam(
        selectedProspect.value.id,
        newTeamId
      )
      if (result.success) {
        const teamName = result.data.responsible_team_name
        // Update selected prospect in modal
        selectedProspect.value = {
          ...selectedProspect.value,
          responsible_team_id: newTeamId,
          responsible_team_name: teamName
        }
        // Update in pending/urgent arrays so computed lists recalculate
        const updateInList = list => {
          const idx = list.findIndex(p => p.id === selectedProspect.value.id)
          if (idx !== -1) {
            list[idx] = {
              ...list[idx],
              responsible_team_id: newTeamId,
              responsible_team_name: teamName
            }
          }
        }
        updateInList(pendingProspects.value)
        updateInList(urgentProspects.value)
      }
    } catch (err) {
      console.error('Error updating team:', err)
    }
  }

  const handleProspectUpdated = updatedProspect => {
    if (selectedProspect.value?.id === updatedProspect.id) {
      selectedProspect.value = { ...selectedProspect.value, ...updatedProspect }
    }
  }

  // ==================== Add Role ====================
  const handleAddRole = async () => {
    if (!selectedProspect.value) return
    isAddingRole.value = true
    addRoleError.value = ''

    try {
      const result = await prospectPreferencesService.create(selectedProspect.value.id, {
        role: '',
        dpw: 1,
        teams: [],
        experience_levels: []
      })

      if (result.success) {
        newlyAddedPrefId.value = result.data?.id || null
        await reloadPreferences()
        // Expand the new role and start in edit mode
        const newIdx = selectedPreferences.value.findIndex(p => p.id === newlyAddedPrefId.value)
        if (newIdx !== -1) {
          expandedPreferences.value[newIdx] = true
        }
      } else {
        addRoleError.value = result.error || 'Fout bij toevoegen'
      }
    } catch (err) {
      console.error('Error adding role:', err)
      addRoleError.value = err.response?.data?.error || 'Fout bij toevoegen van rol'
    } finally {
      isAddingRole.value = false
    }
  }

  const handlePreferenceUpdated = async () => {
    await reloadPreferences()
  }

  const handleDeletePreference = async prefId => {
    if (!selectedProspect.value) return
    try {
      await prospectPreferencesService.delete(selectedProspect.value.id, prefId)
      await reloadPreferences()
    } catch (err) {
      console.error('Error deleting preference:', err)
      alert(err.response?.data?.error || 'Fout bij verwijderen van rol')
    }
  }

  const reloadPreferences = async () => {
    if (!selectedProspect.value) return
    try {
      const prefs = await prospectPreferencesService.getByProspect(selectedProspect.value.id)
      selectedPreferences.value = (prefs || []).map(p => ({
        ...p,
        preferred_employees: Array.isArray(p.preferred_employees)
          ? p.preferred_employees
          : JSON.parse(p.preferred_employees || '[]'),
        teams: Array.isArray(p.teams) ? p.teams : JSON.parse(p.teams || '[]'),
        experience_levels: Array.isArray(p.experience_levels)
          ? p.experience_levels
          : JSON.parse(p.experience_levels || '[]')
      }))
    } catch (err) {
      console.error('Error reloading preferences:', err)
    }
  }

  // ==================== Workflow actions ====================
  const savePlSelections = async userInfo => {
    if (!selectedProspect.value) return

    for (const pref of selectedPreferences.value) {
      const selection = plSelections.value[pref.id]
      if (!selection) continue

      await prospectPreferencesService.updatePlSelection(selectedProspect.value.id, pref.id, {
        employee_id: selection.employeeId,
        dpw: selection.dpw,
        selected_by_name: userInfo.name,
        selected_by_email: userInfo.email
      })
    }
  }

  const invalidateProposalQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['weekly-availability'] }),
      queryClient.invalidateQueries({ queryKey: ['employee-activities'] }),
      queryClient.invalidateQueries({ queryKey: ['assign-availability'] })
    ])
  }

  const heraanvraagProspect = async () => {
    if (!selectedProspect.value) return

    if (!reviewFeedback.value?.trim()) {
      alert('Let op, het is verplicht om feedback in te vullen bij opnieuw aanvragen')
      return
    }

    isProcessing.value = true

    try {
      const userInfo = {
        name: user.value?.name || 'Performance Lead',
        email: user.value?.email || ''
      }
      await workflowService.heraanvraag(
        selectedProspect.value.id,
        userInfo,
        reviewFeedback.value.trim()
      )

      queryClient.invalidateQueries({ queryKey: ['prospects-leads'] })
      invalidateNavBadges(queryClient, { beoordelen: true, concretiseren: true })
      clearStoredConcept(selectedProspect.value?.id)
      await loadData()
      closeModal()
    } catch (error) {
      console.error('Error sending prospect back to commerciant:', error)
      alert(error.response?.data?.error || 'Fout bij opnieuw aanvragen')
    } finally {
      isProcessing.value = false
    }
  }

  const approveProspect = async () => {
    if (!selectedProspect.value) return
    if (!allRolesHaveSelection.value) {
      alert('Selecteer eerst voor elke rol een medewerker')
      return
    }

    isProcessing.value = true
    try {
      const userInfo = {
        name: user.value?.name || 'Performance Lead',
        email: user.value?.email || 'pl@frisseblikken.com'
      }

      // Persist comparison employees to preferred_employees
      await saveComparisonEmployees()

      // Save all PL selections
      await savePlSelections(userInfo)

      // Then approve the workflow
      await workflowService.approve(
        selectedProspect.value.id,
        userInfo,
        reviewFeedback.value || null
      )

      // Invalidate related caches
      queryClient.invalidateQueries({ queryKey: ['prospects-leads'] })
      await invalidateProposalQueries()
      // Invalidate nav badges - beoordelen count decreased, concretiseren count increased
      invalidateNavBadges(queryClient, { beoordelen: true, concretiseren: true })
      clearStoredConcept(selectedProspect.value?.id)
      await loadData()
      closeModal()
    } catch (error) {
      console.error('Error approving:', error)
      alert(error.response?.data?.error || 'Fout bij goedkeuren')
    } finally {
      isProcessing.value = false
    }
  }

  const rejectProspect = async () => {
    if (!selectedProspect.value) return

    if (!reviewFeedback.value || reviewFeedback.value.trim() === '') {
      alert('Let op, het is verplicht om feedback in te vullen waarom je dit voorstel afwijst')
      return
    }

    isProcessing.value = true
    try {
      const userInfo = {
        name: user.value?.name || 'Performance Lead',
        email: user.value?.email || 'pl@frisseblikken.com'
      }

      await workflowService.reject(selectedProspect.value.id, userInfo, reviewFeedback.value)

      queryClient.invalidateQueries({ queryKey: ['prospects-leads'] })
      // Invalidate nav badges - beoordelen count decreased, concretiseren count increased
      invalidateNavBadges(queryClient, { beoordelen: true, concretiseren: true })
      clearStoredConcept(selectedProspect.value?.id)
      await loadData()
      closeModal()
    } catch (error) {
      console.error('Error rejecting:', error)
      alert(error.response?.data?.error || 'Fout bij afwijzen')
    } finally {
      isProcessing.value = false
    }
  }

  const discussProspect = async () => {
    if (!selectedProspect.value) return

    if (!reviewFeedback.value || reviewFeedback.value.trim() === '') {
      alert('Let op, het is verplicht om feedback in te vullen bij Te Bespreken')
      return
    }

    isProcessing.value = true
    try {
      const userInfo = {
        name: user.value?.name || 'Performance Lead',
        email: user.value?.email || 'pl@frisseblikken.com'
      }

      // Persist comparison employees to preferred_employees
      await saveComparisonEmployees()

      // Save any PL selections that exist (not required for te_bespreken)
      await savePlSelections(userInfo)

      // Then mark for discussion
      await workflowService.discuss(
        selectedProspect.value.id,
        userInfo,
        reviewFeedback.value || null
      )

      // Invalidate related caches
      queryClient.invalidateQueries({ queryKey: ['prospects-leads'] })
      await invalidateProposalQueries()
      // Invalidate nav badges - beoordelen count decreased, concretiseren count increased
      invalidateNavBadges(queryClient, { beoordelen: true, concretiseren: true })
      clearStoredConcept(selectedProspect.value?.id)
      await loadData()
      closeModal()
    } catch (error) {
      console.error('Error marking for discussion:', error)
      alert(error.response?.data?.error || 'Fout bij markeren voor bespreking')
    } finally {
      isProcessing.value = false
    }
  }

  // ==================== Status helpers (for Recent tab) ====================
  const getStatusLabel = status => _getWorkflowLabel(status) || status

  const getStatusBadgeClass = getWorkflowStatusBadgeClass

  // ==================== Formatting helpers ====================
  const formatDateTime = dateString => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // ==================== Initialize ====================
  onMounted(async () => {
    try {
      await Promise.all([loadData(), loadEmployees(), loadTeams()])
    } catch (error) {
      console.error('Error initializing PresourcingReview:', error)
    }
  })

  return {
    // Tab & loading
    activeTab,
    isLoading,
    tabs,
    urgentProspects,
    pendingProspects,
    recentProspects,
    myPendingProspects,

    // Modal state
    selectedProspect,
    selectedPreferences,
    loadingPreferences,
    reviewFeedback,
    isProcessing,
    expandedPreferences,

    // Add role
    isAddingRole,
    addRoleError,
    newlyAddedPrefId,

    // Employee data
    employeeMap,
    employeeDetails,
    employeeAvailability,

    // Teams
    teams,

    // PL selections
    plSelections,
    allRolesHaveSelection,
    localProposals,

    // Availability
    availabilityExpanded,
    loadEmployeeAvailability,

    // Employee modal
    showEmployeeModal,
    currentPreference,
    currentPreferenceId,
    currentPreferenceTeam,
    currentPreferenceFunction,
    currentPreferenceDpw,
    currentPreferencePeriodStart,
    currentPreferencePeriodEnd,

    // Comparison employees
    comparisonEmployees,
    employeeGraphExpanded,

    // Concept (local draft)
    hasActiveConcept,
    conceptLoaded,
    conceptSavedAt,
    hasUnsavedChanges,
    saveConceptAsDraft,
    discardConcept,

    // Team helpers
    getTeamCardClass,

    // Toggle helpers
    togglePreferenceExpanded,
    toggleAvailabilityExpanded,
    toggleEmployeeGraph,

    // Modal
    openReviewModal,
    closeModal,

    // Employee selection
    selectEmployee,
    selectComparisonEmployee,
    openEmployeeModal,
    getAssignedEmployeeIds,
    handleEmployeeSelected,
    removeComparisonEmployee,

    // Team update
    handleUpdateTeam,
    handleProspectUpdated,

    // Add role
    handleAddRole,
    handlePreferenceUpdated,
    handleDeletePreference,

    // Workflow actions
    approveProspect,
    rejectProspect,
    discussProspect,
    heraanvraagProspect,

    // Status helpers
    getStatusLabel,
    getStatusBadgeClass,

    // Formatting
    formatDateTime
  }
}
